import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Clock, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils";
import { format } from "date-fns";

const categoryColors = {
  "sun-safety": "bg-orange-100 text-orange-700 border-orange-200",
  "skin-health": "bg-coral-100 text-coral-700 border-coral-200",
  "technology": "bg-purple-100 text-purple-700 border-purple-200",
  "research": "bg-blue-100 text-blue-700 border-blue-200",
  "tips": "bg-green-100 text-green-700 border-green-200"
};

const categoryColorsDark = {
  "sun-safety": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "skin-health": "bg-coral-500/20 text-coral-400 border-coral-500/30",
  "technology": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "research": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "tips": "bg-green-500/20 text-green-400 border-green-500/30"
};

export default function ArticleCard({ article, featured = false }) {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const displayDate = article.published_date || article.created_date;
  const categoryColor = theme === 'dark' 
    ? categoryColorsDark[article.category] || categoryColorsDark['tips']
    : categoryColors[article.category] || categoryColors['tips'];
  
  return (
    <Link to={createPageUrl(`Article?slug=${article.slug}`)}>
      <Card className={`group cursor-pointer overflow-hidden border-2 transition-all duration-300 ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700 hover:border-orange-500'
          : 'bg-white border-gray-200 hover:border-orange-300'
      } hover:shadow-2xl ${
        featured ? 'md:col-span-2 md:row-span-2' : ''
      }`}>
        <div className={featured ? 'md:flex md:min-h-[20rem]' : ''}>
          <div
            className={`relative overflow-hidden ${featured ? 'h-80 md:h-auto md:w-1/2 md:order-2' : 'h-48'}`}
          >
            <img 
              src={article.cover_image || 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&h=500&fit=crop'} 
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            
            {article.featured && (
              <div className="absolute top-4 left-4">
                <Badge className="bg-gradient-to-r from-orange-500 to-coral-500 text-white border-0">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Featured
                </Badge>
              </div>
            )}

            <div className="absolute top-4 right-4">
              <Badge className={`${categoryColor} border`}>
                {article.category?.replace('-', ' ')}
              </Badge>
            </div>
          </div>

          <div className={`p-6 ${featured ? 'md:w-1/2 md:order-1 md:flex md:flex-col md:justify-center' : ''}`}>
          <h3 className={`font-bold mb-3 group-hover:text-orange-500 transition-colors line-clamp-2 ${
            featured ? 'text-2xl' : 'text-xl'
          } ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {article.title}
          </h3>
          
          <p className={`mb-4 line-clamp-2 ${
            featured ? 'text-base' : 'text-sm'
          } ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {article.excerpt}
          </p>

          <div className={`flex items-center justify-between text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{article.read_time || 5} min read</span>
            </div>
            <span>{displayDate ? format(new Date(displayDate), 'MMM d, yyyy') : 'Recently'}</span>
          </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

